/**
 * Hanyu Pinyin tones: tone numbers ⇄ tone marks, and pinyin with the tones removed.
 *
 * The rules, from their sources:
 *
 * - 汉语拼音方案, the Scheme for the Chinese Phonetic Alphabet (adopted 11 February 1958;
 *   text at zh.wikisource.org). §4: the marks ˉ ˊ ˇ ˋ go on the main vowel of a syllable
 *   and the neutral tone is not marked (mā má mǎ mà ma). §5: when a syllable beginning
 *   with a, o or e follows another and the boundary could be misread, an apostrophe
 *   separates them (pi'ao 皮袄). §3 notes: ü is written u after j, q, x and in yu, yue,
 *   yuan, yun, but keeps its dots in nü and lü (4); iou, uei, uen are written iu, ui, un
 *   after an initial (5); 儿 as a final is written r, as in huar (2); ㄝ alone is ê (3).
 * - GB/T 16159-2012, Basic Rules of the Chinese Phonetic Alphabet Orthography, §6.5.1:
 *   in iu and ui the mark goes on the second letter, and a mark on i replaces its dot
 *   (āyí, cèlüè, liúshuǐ); neutral-tone syllables are unmarked (zhuāngjia, qīngchu).
 * - The working form of "the main vowel": a or e takes the mark; failing that, the o of
 *   ou; failing that, the last vowel.
 *
 * Numbered pinyin puts the tone number after its syllable (hao3), with 5, 0 or no number
 * for the neutral tone, and types ü as v or u: on keyboards without it.
 *
 * Joined syllables (Zhong1guo2, Xī'ān, Zhōngguó) are split against the syllable inventory
 * below. Where the letters allow two splits, the scheme's convention decides: inside a
 * word, a syllable starting with a, o or e is always set off by an apostrophe, so without
 * one Xian is a single syllable and fangan is fan + gan. That is also the reading the
 * Library of Congress's examples assume (Changan, Yanan, Zhengan, Jianan: ALA-LC Chinese
 * Romanization Table, 2012, "Connection of syllables", rule 4).
 */

export type Tone = 0 | 1 | 2 | 3 | 4;

/*
 * The combining marks, by code point: written out with String.fromCodePoint because a
 * bare combining character in a string literal is invisible in an editor.
 */
const MACRON = String.fromCodePoint(0x03_04);
const ACUTE = String.fromCodePoint(0x03_01);
const CARON = String.fromCodePoint(0x03_0c);
const GRAVE = String.fromCodePoint(0x03_00);
const BREVE = String.fromCodePoint(0x03_06);
const DIAERESIS = String.fromCodePoint(0x03_08);
const CIRCUMFLEX = String.fromCodePoint(0x03_02);

/** The mark for each tone: none, macron, acute, caron, grave. */
const TONE_MARK = ["", MACRON, ACUTE, CARON, GRAVE] as const;

/** Marks read as a tone. A breve (ă) is the usual stand-in for the third-tone caron (ǎ). */
const MARK_TONE = new Map<string, Tone>([
  [MACRON, 1],
  [ACUTE, 2],
  [CARON, 3],
  [BREVE, 3],
  [GRAVE, 4],
]);

const VOWELS = new Set(["a", "e", "ê", "i", "o", "u", "ü"]);
/** Letters a tone mark may sit on: the vowels, and m or n in 呣 m and 嗯 n, ng. */
const MARKABLE = new Set([...VOWELS, "m", "n"]);
/** A syllable starting with one of these takes an apostrophe after another syllable. */
const VOWEL_START = new Set(["a", "e", "ê", "o"]);
/** Syllables with no vowel (呣 m, 嗯 n and ng, 噷 hm, 哼 hng), read only when toned. */
const NASAL_SYLLABLES = new Set(["m", "n", "ng", "hm", "hng"]);

/**
 * Every syllable of standard Mandarin, by initial: the standard pinyin table of
 * initials × finals, with the rarer dictionary syllables (lo, yo, me, ê, ei, eng, dei,
 * den, nen, nou, pou, miu, diu, dia, lia, nun, chua, rua, zhuai, chuai, tei, kei, zhei,
 * shei) included and modern neologisms (biang, duang) left out.
 */
const SYLLABLE_TABLE = [
  "a o e ê ai ei ao ou an en ang eng er",
  "yi ya yo ye yao you yan yin yang ying yong yu yue yuan yun",
  "wu wa wo wai wei wan wen wang weng",
  "ba bo bai bei bao ban ben bang beng bi bie biao bian bin bing bu",
  "pa po pai pei pao pou pan pen pang peng pi pie piao pian pin ping pu",
  "ma mo me mai mei mao mou man men mang meng mi mie miao miu mian min ming mu",
  "fa fo fei fou fan fen fang feng fu",
  "da de dai dei dao dou dan den dang deng dong di dia die diao diu dian ding du duo dui duan dun",
  "ta te tai tei tao tou tan tang teng tong ti tie tiao tian ting tu tuo tui tuan tun",
  "na ne nai nei nao nou nan nen nang neng nong ni nie niao niu nian nin niang ning nu nuo nuan nun nü nüe",
  "la lo le lai lei lao lou lan lang leng long li lia lie liao liu lian lin liang ling lu luo luan lun lü lüe",
  "ga ge gai gei gao gou gan gen gang geng gong gu gua guo guai gui guan gun guang",
  "ka ke kai kei kao kou kan ken kang keng kong ku kua kuo kuai kui kuan kun kuang",
  "ha he hai hei hao hou han hen hang heng hong hu hua huo huai hui huan hun huang",
  "ji jia jie jiao jiu jian jin jiang jing jiong ju jue juan jun",
  "qi qia qie qiao qiu qian qin qiang qing qiong qu que quan qun",
  "xi xia xie xiao xiu xian xin xiang xing xiong xu xue xuan xun",
  "zhi zha zhe zhai zhei zhao zhou zhan zhen zhang zheng zhong zhu zhua zhuo zhuai zhui zhuan zhun zhuang",
  "chi cha che chai chao chou chan chen chang cheng chong chu chua chuo chuai chui chuan chun chuang",
  "shi sha she shai shei shao shou shan shen shang sheng shu shua shuo shuai shui shuan shun shuang",
  "ri re rao rou ran ren rang reng rong ru rua ruo rui ruan run",
  "zi za ze zai zei zao zou zan zen zang zeng zong zu zuo zui zuan zun",
  "ci ca ce cai cao cou can cen cang ceng cong cu cuo cui cuan cun",
  "si sa se sai sao sou san sen sang seng song su suo sui suan sun",
]
  .join(" ")
  .split(" ");

/**
 * Other spellings of a syllable: ü typed with its dots where pinyin drops them (jü → ju,
 * yüan → yuan), and lue and nue, which can only be lüe and nüe.
 */
const ALIASES: readonly (readonly [string, string])[] = [
  ["lue", "lüe"],
  ["nue", "nüe"],
  ...["j", "q", "x", "y"].flatMap((initial) =>
    ["u", "ue", "uan", "un"].map(
      (final) => [`${initial}ü${final.slice(1)}`, `${initial}${final}`] as const
    )
  ),
];

/** The longest spelling: zhuang plus an erhua r. */
const MAX_SYLLABLE = 7;

/** An erhua syllable — huar, dianr — rather than er itself. */
function isErhua(spelling: string): boolean {
  return spelling !== "er" && spelling.endsWith("r");
}

/** Every accepted spelling, mapped to the syllable it spells; erhua forms included. */
function buildSpellings(): Map<string, string> {
  const spellings = new Map<string, string>();
  const add = (written: string, syllable: string) => {
    spellings.set(written, syllable);
    if (!(syllable.endsWith("r") || VOWEL_START.has(syllable.charAt(0)))) {
      spellings.set(`${written}r`, `${syllable}r`);
    }
  };
  for (const syllable of SYLLABLE_TABLE) {
    add(syllable, syllable);
  }
  for (const [written, syllable] of ALIASES) {
    add(written, syllable);
  }
  return spellings;
}

const SPELLINGS = buildSpellings();

/* ------------------------------------------------------------------ reading */

interface Letter {
  /** a–z, ü or ê, in lower case. */
  base: string;
  upper: boolean;
  /** The tone of a mark written on this letter, or 0. */
  mark: Tone;
}

type Token =
  | { kind: "letter"; letter: Letter }
  | { kind: "digit"; value: number }
  | { kind: "apostrophe"; char: string };

interface Run {
  letters: Letter[];
  /** The tone number typed straight after the run, if any. */
  digit: number | null;
  /** The apostrophe typed before the run, if any. */
  apostrophe: string;
}

interface Syllable {
  /** The letters as typed, case kept, with ü spelled as the syllable spells it. */
  letters: Letter[];
  /** The syllable in lower case: "zhong", "lüe", "huar". */
  spelling: string;
  tone: Tone;
  /** The apostrophe typed before this syllable, if any. */
  apostrophe: string;
}

interface Word {
  syllables: Syllable[];
  /** Whether the word carries a tone number or a tone mark anywhere. */
  toned: boolean;
}

const ASCII_LETTER = /^[a-z]$/i;
const ASCII_DIGIT = /^\d$/;
const COMBINING_MARK = /^\p{M}$/u;
const APOSTROPHES = new Set(["'", "’"]);

/** A word-like run of Latin letters, marks and digits, joined by apostrophes or u:'s colon. */
const WORD = /[\p{Script=Latin}\p{M}\d]+(?:[:'’][\p{Script=Latin}\p{M}\d]+)*/gu;
/** A letter followed by a digit: an attempt at numbered pinyin. */
const NUMBERED_ATTEMPT = /\p{L}\d/u;
const NASAL_LETTER = /[mn]/;
const U_COLON = /([uU]):/g;

/** Put one combining mark on a letter; false when pinyin has no use for it there. */
function applyMark(letter: Letter, mark: string): boolean {
  const tone = MARK_TONE.get(mark);
  if (tone !== undefined) {
    if (letter.mark !== 0 || !MARKABLE.has(letter.base)) {
      return false;
    }
    letter.mark = tone;
    return true;
  }
  if (mark === DIAERESIS && letter.base === "u") {
    letter.base = "ü";
    return true;
  }
  if (mark === CIRCUMFLEX && letter.base === "e") {
    letter.base = "ê";
    return true;
  }
  return false;
}

/** A letter as typed; v stands for ü. */
function newLetter(char: string): Letter {
  const lower = char.toLowerCase();
  return { base: lower === "v" ? "ü" : lower, upper: char !== lower, mark: 0 };
}

/** Letters, tone digits and apostrophes; null when the word holds anything else. */
function tokenize(word: string): Token[] | null {
  const tokens: Token[] = [];
  let current: Letter | null = null;
  // u: is ü: give the u a diaeresis and let it be read like any other mark.
  for (const char of word.normalize("NFD").replace(U_COLON, `$1${DIAERESIS}`)) {
    if (COMBINING_MARK.test(char)) {
      if (!(current && applyMark(current, char))) {
        return null;
      }
      continue;
    }
    current = null;
    if (ASCII_LETTER.test(char)) {
      current = newLetter(char);
      tokens.push({ kind: "letter", letter: current });
    } else if (ASCII_DIGIT.test(char)) {
      tokens.push({ kind: "digit", value: Number(char) });
    } else if (APOSTROPHES.has(char)) {
      tokens.push({ kind: "apostrophe", char });
    } else {
      return null;
    }
  }
  return tokens;
}

/** Group tokens into runs of letters, each closed by a tone digit or an apostrophe. */
function toRuns(tokens: readonly Token[]): Run[] | null {
  const runs: Run[] = [];
  let open: Run | null = null;
  let apostrophe = "";
  for (const token of tokens) {
    if (token.kind === "letter") {
      if (!open) {
        open = { letters: [], digit: null, apostrophe };
        runs.push(open);
        apostrophe = "";
      }
      open.letters.push(token.letter);
    } else if (token.kind === "digit") {
      if (!open || token.value > 5) {
        return null;
      }
      open.digit = token.value;
      open = null;
    } else {
      if (runs.length === 0 || apostrophe !== "") {
        return null;
      }
      apostrophe = token.char;
      open = null;
    }
  }
  return apostrophe !== "" || runs.length === 0 ? null : runs;
}

/** The syllable spelled by letters [start, end), if it carries at most one mark on a vowel. */
function syllableAt(
  letters: readonly Letter[],
  written: string,
  start: number,
  end: number
): string | undefined {
  const spelling = SPELLINGS.get(written.slice(start, end));
  if (!spelling) {
    return;
  }
  let marks = 0;
  for (let index = start; index < end; index++) {
    if (letters[index].mark !== 0) {
      marks += 1;
      if (!VOWELS.has(letters[index].base)) {
        return;
      }
    }
  }
  return marks > 1 ? undefined : spelling;
}

interface Split {
  /** Joined vowel-initial syllables (1 each) and erhua syllables (2 each). */
  cost: number;
  count: number;
  end: number;
  spelling: string;
}

function penalty(spelling: string, start: number): number {
  const vowelStart = start > 0 && VOWEL_START.has(spelling.charAt(0)) ? 1 : 0;
  return vowelStart + (isErhua(spelling) ? 2 : 0);
}

/** The cheapest split of letters [start, …), longest first syllable on a tie. */
function bestFrom(
  letters: readonly Letter[],
  written: string,
  start: number,
  best: readonly (Split | undefined)[]
): Split | undefined {
  let chosen: Split | undefined;
  for (
    let end = Math.min(written.length, start + MAX_SYLLABLE);
    end > start;
    end--
  ) {
    const rest = best[end];
    const spelling = rest
      ? syllableAt(letters, written, start, end)
      : undefined;
    if (rest && spelling) {
      const cost = rest.cost + penalty(spelling, start);
      const count = rest.count + 1;
      if (
        !chosen ||
        cost < chosen.cost ||
        (cost === chosen.cost && count < chosen.count)
      ) {
        chosen = { cost, count, end, spelling };
      }
    }
  }
  return chosen;
}

/** A syllable with no vowel needs a tone, from a digit or a mark on its m or n. */
function nasalToned(letters: readonly Letter[], hasDigit: boolean): boolean {
  const marked = letters.filter((letter) => letter.mark !== 0);
  if (marked.length === 0) {
    return hasDigit;
  }
  return marked.length === 1;
}

/** Split one run of letters into syllable spellings, or null if it is not pinyin. */
function segment(
  letters: readonly Letter[],
  hasDigit: boolean
): string[] | null {
  const written = letters.map((letter) => letter.base).join("");
  if (NASAL_SYLLABLES.has(written)) {
    return nasalToned(letters, hasDigit) ? [written] : null;
  }
  const best: (Split | undefined)[] = [];
  best[written.length] = {
    cost: 0,
    count: 0,
    end: written.length,
    spelling: "",
  };
  for (let start = written.length - 1; start >= 0; start--) {
    best[start] = bestFrom(letters, written, start, best);
  }
  const spellings: string[] = [];
  let at = 0;
  while (at < written.length) {
    const step = best[at];
    if (!step) {
      return null;
    }
    spellings.push(step.spelling);
    at = step.end;
  }
  return spellings;
}

function digitTone(digit: number): Tone {
  return digit >= 1 && digit <= 4 ? (digit as Tone) : 0;
}

function splitRun(run: Run): Syllable[] | null {
  const spellings = segment(run.letters, run.digit !== null);
  if (!spellings) {
    return null;
  }
  const syllables: Syllable[] = [];
  let start = 0;
  for (const [index, spelling] of spellings.entries()) {
    // Spell ü the way the syllable does: xü → xu, lue → lüe.
    const letters = run.letters
      .slice(start, start + spelling.length)
      .map((letter, offset) => ({ ...letter, base: spelling.charAt(offset) }));
    start += spelling.length;
    syllables.push({
      letters,
      spelling,
      tone: letters.find((letter) => letter.mark !== 0)?.mark ?? 0,
      apostrophe: index === 0 ? run.apostrophe : "",
    });
  }
  const last = syllables.at(-1);
  if (last && run.digit !== null) {
    last.tone = digitTone(run.digit);
  }
  return syllables;
}

/** "na3r": an erhua r typed after the tone number joins the syllable before it. */
function isErhuaSuffix(run: Run, previous: Run | undefined): boolean {
  return (
    previous !== undefined &&
    previous.digit !== null &&
    run.apostrophe === "" &&
    run.digit === null &&
    run.letters.length === 1 &&
    run.letters[0].base === "r"
  );
}

function attachErhua(syllables: Syllable[], r: Letter): boolean {
  const last = syllables.at(-1);
  const spelling = last ? SPELLINGS.get(`${last.spelling}r`) : undefined;
  if (!(last && spelling)) {
    return false;
  }
  last.letters.push(r);
  last.spelling = spelling;
  return true;
}

/** A word read as pinyin, or null when any part of it is not. */
function parseWord(word: string): Word | null {
  const tokens = tokenize(word);
  const runs = tokens ? toRuns(tokens) : null;
  if (!runs) {
    return null;
  }
  const syllables: Syllable[] = [];
  for (const [index, run] of runs.entries()) {
    if (isErhuaSuffix(run, runs[index - 1])) {
      if (!attachErhua(syllables, run.letters[0])) {
        return null;
      }
      continue;
    }
    const split = splitRun(run);
    if (!split) {
      return null;
    }
    syllables.push(...split);
  }
  const toned =
    runs.some((run) => run.digit !== null) ||
    syllables.some((syllable) => syllable.tone !== 0);
  return { syllables, toned };
}

/** The syllables a word splits into, in lower case; null if it is not pinyin. */
export function splitSyllables(word: string): string[] | null {
  return (
    parseWord(word)?.syllables.map((syllable) => syllable.spelling) ?? null
  );
}

/* ----------------------------------------------------------------- placement */

export type PlacementRule = "a" | "e" | "ou" | "only" | "last" | "nasal";

/** Which letter of a syllable carries its tone mark, and the rule that picked it. */
export function placeToneMark(spelling: string): {
  index: number;
  rule: PlacementRule;
} {
  const body = isErhua(spelling) ? spelling.slice(0, -1) : spelling;
  const a = body.indexOf("a");
  if (a >= 0) {
    return { index: a, rule: "a" };
  }
  const e = [...body].findIndex((char) => char === "e" || char === "ê");
  if (e >= 0) {
    return { index: e, rule: "e" };
  }
  const ou = body.indexOf("ou");
  if (ou >= 0) {
    return { index: ou, rule: "ou" };
  }
  const vowels = [...body].flatMap((char, index) =>
    VOWELS.has(char) ? [index] : []
  );
  const last = vowels.at(-1);
  if (last === undefined) {
    // 嗯 ńg, 呣 ḿ: no vowel, so the mark sits on the nasal.
    return { index: Math.max(0, body.search(NASAL_LETTER)), rule: "nasal" };
  }
  return { index: last, rule: vowels.length === 1 ? "only" : "last" };
}

/** One letter with a tone mark: markLetter("a", 3) → "ǎ". */
export function markLetter(letter: string, tone: Tone): string {
  return `${letter}${TONE_MARK[tone]}`.normalize("NFC");
}

/* ----------------------------------------------------------------- rendering */

/** How ü is typed in numbered pinyin. */
export type NumberedU = "v" | "u:" | "ü";
/** How ü is written without tones; "yu" is the passport rule (LYU, but LUE). */
export type PlainU = "ü" | "yu" | "v" | "u";

export interface PinyinOptions {
  /** The neutral tone in numbered pinyin: "5", "0" or nothing. */
  neutral: "5" | "0" | "";
  numberedU: NumberedU;
  plainU: PlainU;
}

function letterText(letter: Letter, u: string): string {
  const text = letter.base === "ü" ? u : letter.base;
  return letter.upper ? text.toUpperCase() : text;
}

function markedSyllable(syllable: Syllable): string {
  const { index } = placeToneMark(syllable.spelling);
  let text = "";
  for (const [position, letter] of syllable.letters.entries()) {
    text += letterText(letter, "ü");
    if (position === index) {
      text += TONE_MARK[syllable.tone];
    }
  }
  return text.normalize("NFC");
}

function lettersOf(syllable: Syllable, u: string): string {
  return syllable.letters.map((letter) => letterText(letter, u)).join("");
}

/**
 * National Immigration Administration, announcement of 29 September 2021 (under
 * GB/T 28039-2011): in exit-entry documents ü becomes YU in LÜ and NÜ, and U in LÜE
 * and NÜE.
 */
function passportU(spelling: string): string {
  const body = isErhua(spelling) ? spelling.slice(0, -1) : spelling;
  return body.endsWith("ü") ? "yu" : "u";
}

function plainSyllable(syllable: Syllable, plainU: PlainU): string {
  return lettersOf(
    syllable,
    plainU === "yu" ? passportU(syllable.spelling) : plainU
  );
}

/** The apostrophe before a syllable: the one typed, or the scheme's before a, o, e. */
function apostropheBefore(word: Word, index: number): string {
  const syllable = word.syllables[index];
  if (index === 0) {
    return "";
  }
  if (syllable.apostrophe !== "") {
    return syllable.apostrophe;
  }
  return word.toned && VOWEL_START.has(syllable.spelling.charAt(0)) ? "'" : "";
}

function renderMarks(word: Word): string {
  return word.syllables
    .map(
      (syllable, index) =>
        apostropheBefore(word, index) + markedSyllable(syllable)
    )
    .join("");
}

function renderPlain(word: Word, plainU: PlainU): string {
  return word.syllables
    .map(
      (syllable, index) =>
        apostropheBefore(word, index) + plainSyllable(syllable, plainU)
    )
    .join("");
}

function toneDigit(tone: Tone, neutral: PinyinOptions["neutral"]): string {
  return tone === 0 ? neutral : String(tone);
}

/**
 * Numbered pinyin. A number already marks the boundary after its syllable, so no
 * apostrophe follows one. A word with no tone at all keeps its spelling unless it is a
 * single syllable — a neutral particle such as de or ma — which takes the neutral number.
 */
function renderNumbers(word: Word, options: PinyinOptions): string {
  const numbered = word.toned || word.syllables.length === 1;
  let text = "";
  let afterNumber = false;
  for (const [index, syllable] of word.syllables.entries()) {
    if (!afterNumber) {
      text += apostropheBefore(word, index);
    }
    const digit = numbered ? toneDigit(syllable.tone, options.neutral) : "";
    text += lettersOf(syllable, options.numberedU) + digit;
    afterNumber = digit !== "";
  }
  return text;
}

/* ----------------------------------------------------------------- whole text */

interface Piece {
  text: string;
  word: Word | null;
}

function toPieces(text: string): Piece[] {
  const pieces: Piece[] = [];
  let last = 0;
  for (const match of text.matchAll(WORD)) {
    const start = match.index ?? 0;
    if (start > last) {
      pieces.push({ text: text.slice(last, start), word: null });
    }
    pieces.push({ text: match[0], word: parseWord(match[0]) });
    last = start + match[0].length;
  }
  if (last < text.length) {
    pieces.push({ text: text.slice(last), word: null });
  }
  return pieces;
}

/** A word that was meant as toned pinyin: a letter then a digit, or a tone mark. */
function looksToned(text: string): boolean {
  if (NUMBERED_ATTEMPT.test(text)) {
    return true;
  }
  return [...text.normalize("NFD")].some((char) => MARK_TONE.has(char));
}

export interface WorkingRow {
  /** The syllable in numbered pinyin, lower case: "hao3". */
  numbered: string;
  /** With its tone mark: "hǎo". */
  marked: string;
  rule: PlacementRule;
}

function lowerCase(syllable: Syllable): Syllable {
  return {
    ...syllable,
    letters: syllable.letters.map((letter) => ({ ...letter, upper: false })),
  };
}

/** Each toned syllable once, in order of first appearance. */
function workingRows(
  pieces: readonly Piece[],
  options: PinyinOptions
): WorkingRow[] {
  const rows = new Map<string, WorkingRow>();
  for (const piece of pieces) {
    for (const syllable of piece.word?.syllables ?? []) {
      const lower = lowerCase(syllable);
      const numbered = lettersOf(lower, options.numberedU) + String(lower.tone);
      if (syllable.tone !== 0 && !rows.has(numbered)) {
        rows.set(numbered, {
          numbered,
          marked: markedSyllable(lower),
          rule: placeToneMark(syllable.spelling).rule,
        });
      }
    }
  }
  return [...rows.values()];
}

export interface PinyinResult {
  /** Pinyin with tone marks: "Xī'ān". */
  marks: string;
  /** Pinyin with tone numbers: "Xi1an1". */
  numbers: string;
  /** Pinyin with no tones: "Xi'an". */
  plain: string;
  /** Words with a tone number or mark that could not be read as pinyin; left as typed. */
  unread: string[];
  /** Whether any word carried a tone number or a tone mark. */
  toned: boolean;
  working: WorkingRow[];
}

/** Convert running text; anything that is not pinyin passes through unchanged. */
export function convertPinyin(
  text: string,
  options: PinyinOptions
): PinyinResult {
  const pieces = toPieces(text);
  const render = (convert: (word: Word) => string) =>
    pieces
      .map((piece) => (piece.word ? convert(piece.word) : piece.text))
      .join("");
  const unread = pieces
    .filter((piece) => piece.word === null && looksToned(piece.text))
    .map((piece) => piece.text);
  return {
    marks: render(renderMarks),
    numbers: render((word) => renderNumbers(word, options)),
    plain: render((word) => renderPlain(word, options.plainU)),
    unread: [...new Set(unread)],
    toned: pieces.some((piece) => piece.word?.toned === true),
    working: workingRows(pieces, options),
  };
}
